// Send theme-owned interactions through the already initialized native Google tag.
// Never load/configure a second SDK or create a client/session ID.
(() => {
  if (window.AlAsilGoogleAnalytics) return;
  const measurementId = 'G-XLD5KZYH8E';
  const nativeTagId = 'GT-NC8BDX9C';
  const isReady = () => typeof window.gtag === 'function' && !!window.google_tag_manager?.[nativeTagId];
  const permitted = () => window.AlAsilAnalyticsEnvironment?.enabled !== false &&
    window.navigator?.doNotTrack !== '1' && window.navigator?.globalPrivacyControl !== true &&
    window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const pending = [];
  let pendingTimer = null;
  let documentLeaving = false;
  const delivery = { queued: 0, submitted: 0, expired: 0, overflow: 0, revoked: 0, identity_changed: 0, failed: 0 };
  function clearPending(reason) {
    delivery[reason] += pending.length;
    pending.length = 0;
    if (pendingTimer !== null) clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  function schedulePending() {
    if (pendingTimer !== null || !pending.length) return;
    pendingTimer = setTimeout(() => { pendingTimer = null; flushPending(); }, 200);
  }
  function flushPending() {
    if (!permitted()) { clearPending('revoked'); return; }
    while (pending.length) {
      if (Date.now() - pending[0].at >= 30000 || Date.now() < pending[0].at) {
        pending.shift(); delivery.expired++; continue;
      }
      if (!isReady() || window.navigator?.onLine === false) break;
      const entry = pending.shift();
      // Remove before calling a vendor. A throw can occur after an internal
      // enqueue; retrying an ambiguous handoff would double-count it.
      try { sendNative(entry.eventName, entry.params); delivery.submitted++; }
      catch (_) { delivery.failed++; }
      if (!permitted()) { clearPending('revoked'); break; }
    }
    schedulePending();
  }
  function submitNative(eventName, params) {
    if (!permitted()) return false;
    flushPending();
    if (!permitted()) return false;
    // Retain only sanitized, consented data, with its original public page.
    // No persistence across documents except the existing navigation handoff.
    try {
      const page = new URL(window.location.href);
      const path = publicPath(page);
      if (path && !params.page_location) params.page_location = page.origin + path;
    } catch (_) {}
    if (isReady() && window.navigator?.onLine !== false) {
      try { sendNative(eventName, params); delivery.submitted++; window.AlAsilGoogleAnalytics.lastStatus = 'submitted'; return true; }
      catch (_) { delivery.failed++; return false; }
    }
    // pagehide observers may run after our cleanup (for example attention).
    // They can attempt an immediate handoff, but cannot repopulate a frozen queue.
    if (documentLeaving) return false;
    const entry = { eventName, params, at: Date.now() };
    const bytes = JSON.stringify(entry).length;
    if (bytes > 12000) { delivery.overflow++; return false; }
    while (pending.length >= 40 || pending.reduce((sum, row) => sum + row.bytes, 0) + bytes > 64000) {
      pending.shift(); delivery.overflow++;
    }
    pending.push({ ...entry, bytes }); delivery.queued++;
    window.AlAsilGoogleAnalytics.lastStatus = 'queued';
    schedulePending();
    return true;
  }
  const handoffKey = 'alasil:ga4-navigation:v1';
  let initialHandoff = null;
  try { initialHandoff = window.sessionStorage?.getItem(handoffKey); } catch (_) {}
  const navigationNames = new Set(['select_promotion', 'select_item', 'nav_click']);
  const intents = new WeakMap();
  let intentSequence = 0;
  function publicPath(url) {
    if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return null;
    const decoded = decodeURIComponent(url.pathname);
    if (!/^\/(?:ar\/)?(?:(?:products|collections|pages)\/[^?#]+|search\/?|ar\/?)?$/.test(decoded) ||
        /@|[\u0000-\u001f]|(?:\+?\d[\s().-]*){8,}/.test(decoded)) return null;
    return url.pathname;
  }
  function discard(intent) {
    if (!intent) initialHandoff = null;
    try {
      if (!intent || JSON.parse(window.sessionStorage.getItem(handoffKey) || 'null')?.intent === intent) window.sessionStorage.removeItem(handoffKey);
    } catch (_) { try { window.sessionStorage.removeItem(handoffKey); } catch (_) {} }
  }
  function discardInitial() {
    const raw = initialHandoff;
    initialHandoff = null;
    try { if (window.sessionStorage.getItem(handoffKey) === raw) window.sessionStorage.removeItem(handoffKey); } catch (_) {}
  }
  window.AlAsilAnalyticsNavigation = function (event, link) {
    try {
      if (!permitted() || !event?.isTrusted || event.defaultPrevented || event.button !== 0 ||
          event.detail === 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
          !link || link.hasAttribute('download')) return undefined;
      const target = link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '_self';
      if (target.toLowerCase() !== '_self') return undefined;
      const destination = new URL(link.href, window.location.href);
      const path = publicPath(destination);
      if (!path || path === window.location.pathname || !publicPath(new URL(window.location.href))) return undefined;
      if (intents.has(event)) return intents.get(event);
      const intent = `${Date.now()}-${++intentSequence}`;
      const options = {navigation: {destination: destination.href, intent}};
      intents.set(event, options);
      // Later handlers may cancel the same click. Never prevent or delay it here.
      setTimeout(() => { if (event.defaultPrevented) discard(intent); }, 0);
      setTimeout(() => discard(intent), 30000);
      return options;
    } catch (_) { return undefined; }
  };
  function handoff(eventName, params, navigation) {
    const destination = publicPath(new URL(navigation.destination, window.location.href));
    const source = publicPath(new URL(window.location.href));
    if (!source || !destination || source === destination || !/^\d+-\d+$/.test(navigation.intent || '')) return false;
    let previous;
    try { previous = JSON.parse(window.sessionStorage.getItem(handoffKey) || 'null'); } catch (_) {}
    const entries = previous?.intent === navigation.intent ? previous.entries : [];
    if (!Array.isArray(entries)) return false;
    if (entries.some(entry => entry.eventName === eventName)) return true;
    if (entries.length >= 3) return false;
    entries.push({eventName, params});
    const envelope = {version: 1, intent: navigation.intent, at: Date.now(), origin: window.location.origin, source, destination, entries};
    const serialized = JSON.stringify(envelope);
    if (serialized.length > 12000) return false;
    window.sessionStorage.setItem(handoffKey, serialized);
    return true;
  }
  function sendNative(eventName, params) {
    if (!permitted()) return;
    params = eventPayload(eventName, params);
    if (params.language === 'en' || params.language === 'ar') params.user_properties = {...currentUserProperties(), preferred_language: params.language};
    window.gtag('event', eventName, {...params, send_to: measurementId});
  }
  function consumeNavigation() {
    try {
      // Only an envelope present at this document's initialization is eligible.
      // A click stored later in the source document can never be flushed there.
      const raw = initialHandoff;
      if (!raw) return true;
      if (raw.length > 12000) { discardInitial(); return true; }
      const saved = JSON.parse(raw);
      if (saved?.version !== 1 || !Number.isFinite(saved.at) || Date.now() - saved.at < 0 ||
          Date.now() - saved.at > 30000 || saved.origin !== window.location.origin || saved.destination !== window.location.pathname ||
          saved.source !== publicPath(new URL(saved.source, saved.origin)) || saved.destination !== publicPath(new URL(saved.destination, saved.origin)) ||
          !Array.isArray(saved.entries) || saved.entries.length > 3) { discardInitial(); return true; }
      if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') return false;
      if (!permitted()) { discard(); return true; }
      if (!isReady()) return false;
      // Consume before vendor invocation: no retries or duplicate original-page send.
      discardInitial();
      for (const entry of saved.entries) if (navigationNames.has(entry.eventName) && entry.params && typeof entry.params === 'object') {
        sendNative(entry.eventName, {...payload(entry.params), page_location: saved.origin + saved.source});
      }
      return true;
    } catch (_) { discardInitial(); return true; }
  }
  const loginKey = 'alasil:analytics-login:v1';
  let initialLogin;
  try { initialLogin = window.sessionStorage?.getItem(loginKey); } catch (_) {}
  function clearLogin() {
    initialLogin = null;
    try { window.sessionStorage?.removeItem(loginKey); } catch (_) {}
  }
  window.AlAsilAnalyticsAuthStarted = function () {
    try {
      if (!permitted() || document.body?.dataset.customerAuthenticated !== 'false') return;
      window.sessionStorage.setItem(loginKey, JSON.stringify({at: Date.now()}));
    } catch (_) {}
  };
  document.addEventListener('click', event => {
    const link = event.target?.closest?.('a[href]');
    if (!event.isTrusted || event.defaultPrevented || !link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (link.target && link.target !== '_self')) return;
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === '/customer_authentication/login') window.AlAsilAnalyticsAuthStarted();
    } catch (_) {}
  });
  function consumeLogin() {
    if (!initialLogin) return true;
    try {
      const marker = JSON.parse(initialLogin);
      if (!Number.isFinite(marker?.at) || Date.now() - marker.at < 0 || Date.now() - marker.at > 300000 || document.body?.dataset.customerAuthenticated !== 'true') { clearLogin(); return true; }
      if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') return false;
      if (!permitted()) { clearLogin(); return true; }
      if (!isReady()) return false;
      clearLogin();
      sendNative('login', payload({language: document.documentElement?.lang?.split('-')[0], page_type: document.body?.dataset.analyticsPageType}));
      return true;
    } catch (_) { clearLogin(); return true; }
  }
  let lastUserProperties;
  let selectedEmirate = null;
  const emirates = new Set(['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']);
  function currentUserProperties() {
    const language = document.documentElement?.lang?.split('-')[0];
    if (!document.body && !language) return null;
    const properties = {customer_type: null, lifetime_orders: null, lifetime_value_band: null, emirate: selectedEmirate};
    if (['en', 'ar'].includes(language)) properties.preferred_language = language;
    if (document.body?.dataset.customerAuthenticated === 'true') {
      try {
        const context = JSON.parse(document.getElementById?.('AlAsilCustomerAnalyticsContext')?.textContent || '{}');
        if (Number.isSafeInteger(context.lifetime_orders) && context.lifetime_orders >= 0 && context.lifetime_orders <= 1000000) {
          properties.lifetime_orders = context.lifetime_orders;
          // "Returning" means a known prior order, not GA's returning visitor.
          properties.customer_type = context.lifetime_orders > 0 ? 'returning' : 'new';
        }
        if (/^[A-Z]{3}:(0|0-999|1000-4999|5000-19999|20000\+)$/.test(context.lifetime_value_band || '')) properties.lifetime_value_band = context.lifetime_value_band;
      } catch (_) {}
    }
    return properties;
  }
  function setPreferredLanguage() {
    try {
      if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') return false;
      if (!permitted()) { lastUserProperties = undefined; selectedEmirate = null; return true; }
      if (!isReady()) return false;
      const properties = currentUserProperties();
      if (!properties) return true;
      const signature = JSON.stringify(properties);
      if (signature === lastUserProperties) return true;
      window.gtag('set', {user_properties: properties});
      lastUserProperties = signature;
      return true;
    } catch (_) { return true; }
  }
  document.addEventListener('visitorConsentCollected', () => {
    setPreferredLanguage();
    if (!permitted()) { clearPending('revoked'); discard(); clearLogin(); } else { consumeNavigation(); consumeLogin(); flushPending(); }
  });
  let authenticated = document.body?.dataset.customerAuthenticated === 'true';
  document.addEventListener('alasil:customer-session-account-changed', event => {
    clearPending('identity_changed');
    // Navigation storage is a separate queue and must not cross account changes.
    discard();
    const wasAuthenticated = authenticated;
    authenticated = document.body?.dataset.customerAuthenticated === 'true';
    selectedEmirate = null;
    clearLogin();
    setPreferredLanguage();
    // This owner fires only after fetching and committing server-rendered state.
    // Never infer a provider or a newly created account from the return URL.
    if (!wasAuthenticated && authenticated && event.detail?.previousAuthenticated === false && event.detail?.authenticated === true && permitted()) {
      submitNative('login', payload({language: document.documentElement?.lang?.split('-')[0], page_type: document.body?.dataset.analyticsPageType}));
    }
  });
  const allowedNames = new Set(['search_suggest_viewed', 'select_item', 'search_results_viewed',
    'filter_applied', 'filter_removed', 'sort_changed', 'load_more', 'view_item_list',
    'view_promotion', 'select_promotion', 'variant_selected', 'coupon_clicked', 'coupon_applied',
    'bnpl_info_viewed', 'add_to_wishlist', 'compare_added', 'product_tab_viewed',
    'product_question_opened', 'product_question_submitted', 'location_set', 'contact_click', 'nav_click', 'cart_add_context']);
  const stringKeys = ['language', 'page_type', 'search_term', 'search_type', 'item_list_name', 'item_list_id',
    'filter_name', 'filter_value', 'sort_by', 'load_type', 'list_source', 'promotion_id', 'promotion_name', 'creative_slot',
    'option', 'from', 'to', 'from_product_id', 'product_id', 'variant_id', 'coupon', 'source', 'delivery_promise', 'provider',
    'tab', 'emirate', 'channel', 'nav_type', 'item', 'currency', 'cart_type', 'button_location', 'step_name', 'shipping_tier', 'model_family', 'storage', 'color', 'region_version'];
  const numberKeys = ['suggestions_count', 'results_count', 'index', 'items_count', 'price_delta',
    'discount', 'value', 'step_index'];
  // Destination-specific contracts keep unrelated fields out of an event and
  // leave room below GA's 25-parameter limit. Never trim a full payload by order.
  const eventFields = {
    search_suggest_viewed: ['search_term', 'search_type', 'suggestions_count'],
    search_results_viewed: ['search_term', 'search_type', 'results_count'],
    select_item: ['item_list_name', 'item_list_id', 'search_term', 'search_type', 'index'],
    filter_applied: ['filter_name', 'filter_value'], filter_removed: ['filter_name', 'filter_value'],
    sort_changed: ['sort_by'], load_more: ['load_type', 'items_count', 'item_list_name', 'item_list_id'],
    view_item_list: ['item_list_name', 'item_list_id', 'list_source', 'items_count'],
    view_promotion: ['promotion_id', 'promotion_name', 'creative_slot'],
    select_promotion: ['promotion_id', 'promotion_name', 'creative_slot'],
    variant_selected: ['option', 'from', 'to', 'from_product_id', 'price_delta'],
    coupon_clicked: ['coupon', 'source', 'discount', 'value', 'button_location'],
    coupon_applied: ['coupon', 'source', 'discount', 'value', 'button_location'],
    bnpl_info_viewed: ['provider', 'button_location'], add_to_wishlist: ['source'], compare_added: ['source'],
    product_tab_viewed: ['tab'], product_question_opened: ['source'], product_question_submitted: ['source'],
    location_set: ['emirate'], contact_click: ['channel', 'source'], nav_click: ['nav_type', 'item'],
    cart_add_context: ['button_location'], view_cart: ['cart_type', 'items_count', 'value', 'delivery_promise'],
    cart_drawer_viewed: ['cart_type', 'surface', 'items_count', 'value', 'detail_truncated'], cart_page_viewed: ['cart_type', 'surface', 'items_count', 'value', 'detail_truncated'],
    checkout_step_viewed: ['step_name', 'step_index'], checkout_step_completed: ['step_name', 'step_index'],
    login_prompt_shown: ['step_name', 'step_index'], continue_as_guest: [], login: ['method'],
    fulfillment_selected: ['shipping_tier'], express_checkout_clicked: ['method', 'button_location'],
    checkout_error: ['error_stage', 'error_code', 'duration_ms', 'invalid_fields'],
    page_attention: ['segment_id', 'active_ms', 'visible_ms', 'elapsed_ms', 'reason', 'page_path'],
    page_view: [], view_item: ['value'],
  };
  // Browser observations of the drawer. These never assert payment or a
  // second commerce conversion, even after a successful server preparation.
  const checkoutDiagnostics = {
    cart_continue_clicked: 'cart_continue_clicked', checkout_back_clicked: 'checkout_step_back',
    contact_skipped: 'checkout_contact_skipped', drawer_closed: 'cart_drawer_closed',
    fulfillment_persisted: 'fulfillment_persisted', auth_signin_clicked: 'login_started',
    auth_resume_started: 'account_resume_started', auth_resume_completed: 'account_resume_completed',
    bridge_completed: 'checkout_prepare_completed', redirecting: 'checkout_handoff',
    redirecting_fallback: 'checkout_handoff', checkout_requested: 'checkout_requested',
    address_selected: 'checkout_address_selected', pickup_availability_checked: 'pickup_availability_checked',
    express_checkout_toggled: 'express_checkout_toggled', customer_address_save: 'customer_address_save',
    cart_updated: 'checkout_cart_updated', drawer_started: 'checkout_drawer_started',
    discount_requested: 'discount_requested', discount_result: 'discount_result',
  };
  const diagnosticStepFields = ['step_name', 'step_index'];
  Object.assign(eventFields, {
    cart_continue_clicked: [...diagnosticStepFields, 'interaction_source'],
    checkout_step_back: [...diagnosticStepFields, 'from_step', 'to_step', 'interaction_source'],
    checkout_contact_skipped: [...diagnosticStepFields, 'reason', 'signed_in'],
    cart_drawer_closed: [...diagnosticStepFields, 'step', 'elapsed_ms'],
    fulfillment_persisted: [...diagnosticStepFields, 'method', 'selection', 'success'],
    login_started: [...diagnosticStepFields, 'signed_in'], account_resume_started: [...diagnosticStepFields, 'signed_in'],
    account_resume_completed: [...diagnosticStepFields, 'signed_in', 'success'],
    checkout_prepare_completed: ['success', 'method', 'duration_ms', 'cart_to_checkout_ms'],
    checkout_handoff: ['success', 'method', 'handoff_type', 'cart_to_checkout_ms'],
    checkout_requested: ['method', 'cart_to_checkout_ms'],
    checkout_address_selected: [...diagnosticStepFields, 'success', 'address_source', 'method'],
    pickup_availability_checked: ['success', 'outcome', 'duration_ms', 'method', 'error_code', 'error_stage'],
    express_checkout_toggled: [...diagnosticStepFields, 'expanded', 'success'],
    customer_address_save: ['attempted', 'success', 'location_saved', 'address_source', 'error_code', 'error_stage'],
    checkout_cart_updated: ['success', 'duration_ms', 'method'], checkout_drawer_started: ['signed_in', 'method'],
    discount_requested: ['operation', 'discount_kind'],
    discount_result: ['operation', 'discount_kind', 'outcome', 'success', 'error_code', 'error_stage', 'duration_ms'],
    quantity_change_requested: [...diagnosticStepFields, 'operation', 'requested_quantity', 'variant_id'],
    remove_requested: [...diagnosticStepFields, 'operation', 'requested_quantity', 'variant_id'],
    mutation_succeeded: [...diagnosticStepFields, 'operation', 'requested_quantity', 'result_quantity', 'item_count', 'variant_id', 'success', 'duration_ms'],
    mutation_failed: [...diagnosticStepFields, 'operation', 'requested_quantity', 'variant_id', 'success', 'error_stage', 'error_code', 'duration_ms'],
    render_failed: [...diagnosticStepFields, 'operation', 'variant_id', 'success', 'error_stage', 'error_code', 'duration_ms'],
    note_changed: [...diagnosticStepFields, 'operation', 'changed'], note_saved: [...diagnosticStepFields, 'operation', 'success', 'duration_ms'],
    note_save_failed: [...diagnosticStepFields, 'operation', 'success', 'duration_ms', 'error_stage', 'error_code'],
    checkout_clicked: [...diagnosticStepFields, 'method'], continue_shopping_clicked: diagnosticStepFields,
    product_clicked: [...diagnosticStepFields, 'product_id', 'variant_id'], login_clicked: diagnosticStepFields,
  });
  function checkoutDiagnostic(data) {
    const out = payload({language: data.language, page_type: data.page_type});
    const steps = ['cart', 'identity', 'address', 'contact', 'checkout'];
    const enums = {step: steps, from_step: steps, to_step: steps, interaction_source: ['footer', 'progress_bar', 'account_link'],
      reason: ['account_contact_ready'], method: ['shipping', 'pickup', 'unknown'], selection: ['shipping', 'pickup'],
      outcome: ['available', 'unavailable', 'checking', 'error', 'confirmed', 'rejected', 'unknown'], operation: ['apply', 'remove'], discount_kind: ['unknown'], address_source: ['saved', 'new']};
    for (const [key, values] of Object.entries(enums)) if (values.includes(data[key])) out[key] = data[key];
    for (const key of ['success', 'signed_in', 'expanded', 'attempted', 'location_saved']) if (typeof data[key] === 'boolean') out[key] = data[key];
    for (const key of ['duration_ms', 'cart_to_checkout_ms', 'elapsed_ms']) if (Number.isFinite(data[key]) && data[key] >= 0 && data[key] <= 1800000) out[key] = Math.floor(data[key]);
    const step = {cart: ['cart', 1], identity: ['account', 2], address: ['delivery', 3], contact: ['contact', 4], checkout: ['checkout', 5]}[data.step || data.from_step];
    if (step) { out.step_name = step[0]; out.step_index = step[1]; }
    if (data.stage === 'pickup_availability_checked' && data.success === false) out.error_code = ['pickup_availability_failed', 'pickup_availability_timeout'].includes(data.error_code) ? data.error_code : 'pickup_availability_failed';
    if (data.stage === 'customer_address_save' && data.success === false && data.attempted === true) out.error_code = 'address_save_failed';
    if (['discount_requested', 'discount_result'].includes(data.stage) && ['provider_rejected', 'transport_unconfirmed', 'response_invalid', 'confirmation_unavailable'].includes(data.error_code)) out.error_code = data.error_code;
    if (out.error_code) out.error_stage = data.stage;
    if (data.stage === 'redirecting' || data.stage === 'redirecting_fallback') out.handoff_type = data.stage === 'redirecting' ? 'bridge' : 'fallback';
    return out;
  }
  const surfaceEventSuffix = {
    checkout_step_viewed: 'step_viewed', checkout_step_completed: 'step_completed', checkout_step_back: 'back',
    checkout_contact_skipped: 'contact_skipped', cart_drawer_closed: 'closed',
    checkout_prepare_completed: 'prepare_completed', checkout_handoff: 'handoff', checkout_requested: 'checkout_requested',
    checkout_address_selected: 'address_selected', checkout_cart_updated: 'cart_updated',
    checkout_drawer_started: 'started', checkout_error: 'error', cart_continue_clicked: 'continue_clicked',
  };
  const cartControlStages = new Set(['quantity_change_requested', 'remove_requested', 'mutation_succeeded', 'mutation_failed',
    'note_changed', 'note_saved', 'note_save_failed', 'checkout_clicked', 'continue_shopping_clicked', 'product_clicked', 'login_clicked', 'discount_requested', 'discount_result', 'render_failed']);
  const legacyCheckoutStages = new Set(['address_selected', 'pickup_availability_checked', 'fulfillment_selection_failed', 'fulfillment_selected', 'drawer_started', 'identity_viewed', 'address_viewed', 'contact_viewed', 'login_prompt_shown', 'checkout_step_completed', 'identity_guest_selected', 'express_checkout_toggled', 'address_resolution_failed', 'customer_address_save', 'cart_updated', 'cart_update_failed', 'checkout_requested', 'bridge_completed', 'bridge_failed', 'redirecting', 'redirecting_fallback', 'address_validation_failed', 'express_checkout_clicked', 'auth_signin_clicked', 'auth_resume_started', 'auth_resume_completed', 'cart_continue_clicked', 'checkout_back_clicked', 'contact_validation_failed', 'contact_skipped', 'drawer_closed', 'fulfillment_persisted', 'fulfillment_persist_failed']);
  const drawerControlStages = new Set(['quantity_change_requested', 'remove_requested', 'mutation_succeeded', 'mutation_failed', 'note_changed', 'note_saved', 'note_save_failed', 'render_failed']);
  function permittedCheckoutStage(wrapper, stage) {
    if (typeof stage !== 'string') return false;
    if (wrapper === 'alasil:cart_page_flow') return cartControlStages.has(stage);
    if (wrapper === 'alasil:cart_drawer_flow') return legacyCheckoutStages.has(stage) || drawerControlStages.has(stage) || ['discount_requested', 'discount_result'].includes(stage);
    return wrapper === 'alasil:checkout_flow' && legacyCheckoutStages.has(stage);
  }
  function submitCheckoutObservation(wrapper, eventName, params, stage) {
    if (wrapper === 'alasil:checkout_flow') return submitNative(eventName, params);
    const surface = wrapper === 'alasil:cart_page_flow' ? 'cart_page' : 'cart_drawer';
    const distinctName = surface + '_' + (surfaceEventSuffix[eventName] || eventName);
    if (distinctName.length > 40) return false;
    eventFields[distinctName] = [...(eventFields[eventName] || []), 'surface', 'stage'];
    return submitNative(distinctName, {...params, surface, stage});
  }
  function cartControlDiagnostic(data) {
    const out = {...checkoutDiagnostic(data), step_name: 'cart', step_index: 1};
    if (['mutation_failed', 'note_save_failed', 'render_failed'].includes(data.stage)) out.error_stage = data.stage;
    if (['standard', 'express', 'unknown'].includes(data.method)) out.method = data.method;
    if (['quantity_change', 'change', 'remove', 'note', 'apply'].includes(data.operation)) out.operation = data.operation;
    for (const key of ['changed', 'note_present']) if (typeof data[key] === 'boolean') out[key] = data[key];
    for (const key of ['requested_quantity', 'previous_quantity', 'result_quantity', 'quantity', 'item_count', 'line_index']) if (Number.isSafeInteger(data[key]) && data[key] >= 0 && data[key] <= 10000) out[key] = data[key];
    for (const key of ['product_id', 'variant_id']) if (/^\d{1,30}$/.test(String(data[key] || ''))) out[key] = String(data[key]);
    if (['provider_rejected', 'transport_unconfirmed', 'response_invalid', 'render_failed', 'cart_update_failed', 'cart_update_timeout', 'other_error', 'confirmation_unavailable'].includes(data.error_code)) out.error_code = data.error_code;
    return out;
  }
  const productEventNames = new Set(['select_item', 'view_item', 'view_promotion', 'select_promotion',
    'variant_selected', 'coupon_clicked', 'coupon_applied', 'bnpl_info_viewed', 'add_to_wishlist',
    'compare_added', 'product_tab_viewed', 'product_question_opened', 'product_question_submitted', 'cart_add_context']);
  function eventPayload(name, data) {
    const keys = ['language', 'page_type', 'currency', 'items', 'page_location'];
    if (name === 'page_view') keys.push('page_referrer');
    if (productEventNames.has(name)) keys.push('product_id', 'variant_id', 'model_family', 'storage', 'color', 'region_version', 'delivery_promise', 'source');
    keys.push(...(eventFields[name] || []));
    const out = {};
    for (const key of keys) if (data[key] !== undefined) out[key] = data[key];
    return out;
  }
  const checkoutFailureCodes = {
    bridge_failed: ['checkout_bridge_failed', 'checkout_bridge_timeout', 'checkout_consent_changed'],
    cart_update_failed: ['cart_update_failed', 'cart_update_timeout'],
    address_resolution_failed: ['address_resolution_failed'],
    address_validation_failed: ['address_validation_failed', 'location_required', 'building_required', 'unit_required'],
    fulfillment_selection_failed: ['pickup_unavailable'],
    contact_validation_failed: ['contact_invalid'],
    fulfillment_persist_failed: ['fulfillment_save_failed'],
  };
  function clean(value, identifier) {
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;
    const s = String(value).trim();
    // GA standard properties accept 100 characters for ordinary parameter
    // values. Never truncate an identifier into a different identity. Inspect
    // the full text for private content before shortening public labels.
    if (identifier) return /^[a-zA-Z0-9_:\/.-]{1,100}$/.test(s) ? s : undefined;
    const label = s.replace(/[\u0000-\u001f\u007f]/g, '').trim();
    if (!label || label.length > 4096) return undefined;
    let inspected = label;
    for (let i = 0; i < 3 && /%[a-f0-9]{2}/i.test(inspected); i++) {
      inspected = inspected.replace(/(?:%[a-f0-9]{2})+/gi, part => {
        try { return decodeURIComponent(part); } catch (_) { return '\uFFFD'; }
      });
    }
    inspected = inspected.replace(/[\u0000-\u001f\u007f]/g, '');
    if (/@|https?:\/\/|(?:mailto|tel):|%[a-f0-9]{2}|\uFFFD|(?:\+?\d[\s().-]*){8,}/i.test(inspected)) return undefined;
    return Array.from(label).filter(char => !/^[\uD800-\uDFFF]$/.test(char)).slice(0, 100).join('') || undefined;
  }
  function payload(data) {
    const out = {};
    for (const key of stringKeys) {
      const value = clean(data[key], /(?:^|_)id$/.test(key));
      if (value !== undefined) out[key] = value;
    }
    for (const key of numberKeys) if (typeof data[key] === 'number' && Number.isFinite(data[key])) out[key] = data[key];
    if (Array.isArray(data.items)) out.items = data.items.slice(0, 200).filter(item => item && typeof item === 'object').map(item => {
      const row = {};
      for (const key of ['item_id', 'product_id', 'variant_id', 'sku', 'item_name', 'item_brand', 'item_category',
        'item_category2', 'item_category3', 'item_group_id', 'sim_type', 'stock_status', 'delivery_promise', 'item_variant', 'item_list_name', 'item_list_id', 'model_family', 'storage', 'color', 'region_version']) {
        const value = clean(item[key], /(?:^|_)id$/.test(key) || key === 'sku');
        if (value !== undefined) row[key] = value;
      }
      for (const key of ['price', 'quantity', 'index', 'discount', 'compare_at_price', 'discount_pct']) if (typeof item[key] === 'number' && Number.isFinite(item[key])) row[key] = item[key];
      return row;
    }).filter(item => item.item_id || item.product_id);
    return out;
  }

  window.AlAsilGoogleAnalytics = function (name, data, options) {
    try {
      window.AlAsilGoogleAnalytics.lastStatus = 'not_accepted';
      if (!permitted()) { clearPending('revoked'); return false; }
      if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
      if (name === 'alasil:page_attention') {
        const attention = window.AlAsilAnalyticsPayload?.(name, data);
        if (!attention?.page_path) return false;
        attention.page_location = window.location.origin + attention.page_path;
        return submitNative('page_attention', attention);
      }
      let eventName;
      let details = data;
      if (name === 'alasil:storefront_interaction' && allowedNames.has(data.event_name)) {
        eventName = data.event_name;
        // Native Google owns initial list views; theme owns successful appended pages.
        if (eventName === 'view_item_list' && data.list_source !== 'load_more') return false;
      } else if (name === 'alasil:cart_drawer_viewed' || name === 'alasil:cart_page_viewed') {
        const surface = name === 'alasil:cart_page_viewed' ? 'cart_page' : 'cart_drawer';
        const view = {...payload({...data, cart_type: surface === 'cart_page' ? 'page' : 'drawer', items_count: data.item_count ?? data.items_count}), surface};
        if (data.detail_truncated === true || (Array.isArray(data.items) && data.items.length > 200)) view.detail_truncated = true;
        // Shopify owns the page's standard view_cart. The drawer retains its
        // existing canonical owner and also emits a separately named diagnostic.
        const canonical = surface === 'cart_drawer' ? submitNative('view_cart', view) : false;
        const diagnostic = submitNative(surface + '_viewed', view);
        return canonical || diagnostic;
      } else if (['alasil:checkout_flow', 'alasil:cart_drawer_flow', 'alasil:cart_page_flow'].includes(name)) {
        if (!permittedCheckoutStage(name, data.stage)) return false;
        if (name !== 'alasil:checkout_flow' && cartControlStages.has(data.stage)) {
          return submitCheckoutObservation(name, data.stage, cartControlDiagnostic(data), data.stage);
        }
        if (data.success === false && Object.prototype.hasOwnProperty.call(checkoutFailureCodes, data.stage)) {
          // Final failure after the operation owner's retries, not each retry or
          // successful fallback. Never transmit arbitrary backend error text.
          const codes = checkoutFailureCodes[data.stage];
          const error = {...payload({language: data.language, page_type: data.page_type}),
            error_stage: data.stage, error_code: codes.includes(data.error_code) ? data.error_code : codes[0]};
          if (Number.isFinite(data.duration_ms) && data.duration_ms >= 0 && data.duration_ms <= 180000) error.duration_ms = Math.floor(data.duration_ms);
          if (data.stage === 'contact_validation_failed' && Array.isArray(data.invalid_fields)) {
            error.invalid_fields = [...new Set(data.invalid_fields.filter(key => ['email', 'first_name', 'last_name', 'phone'].includes(key)))].join(',');
          }
          return submitCheckoutObservation(name, 'checkout_error', error, data.stage);
        }
        if (checkoutDiagnostics[data.stage]) return submitCheckoutObservation(name, checkoutDiagnostics[data.stage], checkoutDiagnostic(data), data.stage);
        const steps = {identity_viewed: ['account', 2], address_viewed: ['delivery', 3], contact_viewed: ['contact', 4]};
        if (steps[data.stage]) {
          eventName = 'checkout_step_viewed'; details = {...data, step_name: steps[data.stage][0], step_index: steps[data.stage][1]};
        } else if (data.stage === 'checkout_step_completed' && ['identity', 'address', 'contact'].includes(data.step)) {
          const completed = {identity: ['account', 2], address: ['delivery', 3], contact: ['contact', 4]}[data.step];
          eventName = 'checkout_step_completed'; details = {...data, step_name: completed[0], step_index: completed[1]};
        } else if (data.stage === 'login_prompt_shown' && data.signed_in === false) {
          eventName = 'login_prompt_shown'; details = {...data, step_name: 'account', step_index: 2};
        } else if (data.stage === 'identity_guest_selected') eventName = 'continue_as_guest';
        else if (data.stage === 'fulfillment_selected' && data.success === true) {
          eventName = 'fulfillment_selected'; details = {...data, shipping_tier: data.selection === 'pickup' ? 'store_pickup' : 'delivery'};
        } else if (data.stage === 'express_checkout_clicked') eventName = 'express_checkout_clicked';
      }
      if (!eventName) return false;
      const params = payload(details);
      if (eventName === 'express_checkout_clicked' && ['google_pay', 'apple_pay', 'shop_pay', 'paypal'].includes(data.method)) params.method = data.method;
      if (eventName === 'location_set' && emirates.has(params.emirate)) {
        selectedEmirate = params.emirate;
        setPreferredLanguage();
      }

      if (navigationNames.has(eventName) && options?.navigation) {
        try { if (handoff(eventName, params, options.navigation)) { window.AlAsilGoogleAnalytics.lastStatus = 'navigation_handoff'; return true; } } catch (_) { /* Storage unavailable: retain best-effort immediate send. */ }
      }
      // Native runtime retains identity, page context and consent ownership.
      if (name === 'alasil:cart_drawer_flow' || name === 'alasil:cart_page_flow') return submitCheckoutObservation(name, eventName, params, data.stage);
      return submitNative(eventName, params);
    } catch (_) {
      return false;
    }
  };
  window.AlAsilGoogleAnalytics.isReady = isReady;
  window.AlAsilGoogleAnalytics.getDeliveryState = () => ({ ...delivery, pending: pending.length });
  window.addEventListener?.('online', flushPending);
  window.addEventListener?.('pageshow', () => { documentLeaving = false; flushPending(); });
  window.addEventListener?.('pagehide', () => { documentLeaving = true; clearPending('expired'); });
  // Shopify's native product_viewed is emitted on document navigation, not on
  // this theme's linked-product DOM commit. Observe that owner, never history
  // mutations (filters and same-product variant URLs are not product views).
  const productMain = () => document.querySelector?.('[data-analytics-product-id][id^="MainProduct-"]');
  let viewedProduct = productMain()?.dataset.analyticsProductId;
  let viewedPath = window.location?.pathname;
  document.addEventListener('linkedOptionsLoaded', function (event) {
    const previousProduct = viewedProduct;
    const previousPath = viewedPath;
    const main = productMain();
    viewedProduct = main?.dataset.analyticsProductId;
    viewedPath = window.location?.pathname;
    // Advance the observed state even when denied: granting consent cannot
    // replay a previously rejected navigation or its duplicate notification.
    try {
      if (!previousProduct || !viewedProduct || previousProduct === viewedProduct || previousPath === viewedPath ||
          !permitted()) return;
      const committed = new URL(event.detail?.url, window.location.href);
      if (committed.pathname !== viewedPath || !publicPath(committed)) return;
      const item = window.AlAsilProductAnalyticsItem?.(viewedProduct);
      const currency = window.Shopify?.currency?.active;
      if (!item?.item_id || !item.variant_id || !Number.isFinite(item.price) || item.price < 0 || !/^[A-Z]{3}$/.test(currency || '')) return;
      const details = payload({items: [{...item, quantity: 1}], value: item.price, currency,
        language: document.documentElement?.lang?.split('-')[0], page_type: 'product'});
      // This rollout switch must be enabled only after GA's history-based
      // page views are disabled. Native initial-document page_view stays on.
      if (document.body?.dataset.analyticsManualHistory === 'true') {
        const page = {language: details.language, page_type: 'product', page_location: committed.origin + viewedPath};
        if (previousPath && publicPath(new URL(previousPath, committed.origin))) page.page_referrer = committed.origin + previousPath;
        submitNative('page_view', page);
      }
      submitNative('view_item', details);
    } catch (_) { /* Observation must not affect the committed purchase state. */ }
  });
  // Retry readiness only; vendor invocation is not a delivery acknowledgement.
  let checks = 0;
  let notified = false;
  function checkReady() {
    flushPending();
    const complete = consumeNavigation();
    const loginComplete = consumeLogin();
    const preferenceComplete = setPreferredLanguage();
    if (isReady() && !notified) {
      notified = true;
      document.dispatchEvent(new CustomEvent('alasil:google-analytics-ready'));
    }
    if ((!isReady() || !complete || !preferenceComplete || !loginComplete) && ++checks < 150) {
      setTimeout(checkReady, 200);
    } else if (!complete) {
      discardInitial();
    }
    if (checks >= 150 && !loginComplete) clearLogin();
  }
  checkReady();
})();
